import { Suspense } from "react"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ChMedicationDrawer } from "@/modules/patients/detail/medication/ch/ch-medication-drawer"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { MedicationDetail } from "@/modules/patients/detail/medication/components/ch-medication-search"
import { ChMedicationSearchWrapper } from "@/modules/patients/detail/medication/components/ch-medication-search-wrapper"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { useChMedicationContext } from "@/modules/patients/detail/medication/store/useChMedicationStore"
import { ChMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { getRecommendedMedications } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useCreateRecommendedMedication } from "@/modules/patients/detail/recommended-medications/services/useCreateRecommendedMedication"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Language } from "@/types/__generated__/union-types"

type ChRecommendedMedicationsSearchProps = {
  progressNote: ProgressNoteData
  patientLanguage: Language
  onCloseMenu: VoidFunction
  diagnosisCode: string
}

const useFormatExistingMedications = (progressNote: ProgressNoteData) => {
  const { activeMedications, inactiveMedications } = useMedications<ChMedicationFragmentInlineList>()

  const medications = getRecommendedMedications(progressNote)

  return [
    {
      title: "patient.medication.active",
      options: activeMedications.filter(
        (medication) => !medications.some((m) => m.node.medication.id === medication.id),
      ),
    },
    {
      title: "patient.medication.inactive",
      options: inactiveMedications.filter(
        (medication) => !medications.some((m) => m.node.medication.id === medication.id),
      ),
    },
  ]
}

export const ChRecommendedMedicationsSearch = (props: ChRecommendedMedicationsSearchProps) => {
  const { onCloseMenu, patientLanguage, progressNote, diagnosisCode } = props

  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const setChMedicationDrawerMode = useChMedicationContext((state) => state.setChMedicationDrawerMode)
  const createRecommendedMedication = useCreateRecommendedMedication("CH")

  const existingMedications = useFormatExistingMedications(progressNote)

  const handleCreateRecommendedMedication = (medicationId: string) => {
    createRecommendedMedication({
      medicationId,
      patientId,
      progressNoteId: progressNote.id,
      onCompleted: onCloseMenu,
      onError: onCloseMenu,
    })
  }

  const handleOpenAddMedicalDrawer = (options: {
    editMedication?: chMedicationFragmentInline$data
    freeTextMedicationName?: string
    medication?: MedicationDetail
  }) => {
    const { editMedication, freeTextMedicationName, medication } = options
    openDrawerWithoutConfirmation(
      () => (
        <ChMedicationDrawer
          recommendedMedication={{
            editMedication,
            freeTextMedicationName,
            medication,
            progressNoteId: progressNote.id,
            onAddSuccess: handleCreateRecommendedMedication,
          }}
          diagnosisCode={diagnosisCode}
        />
      ),
      "MedicationDrawer",
      { onBeforeOpen: () => setChMedicationDrawerMode(MEDICATION_MODES.CREATION) },
    )
    onCloseMenu()
  }

  const onSelectFreeTextMedication = (freeTextMedicationName: string) => {
    handleOpenAddMedicalDrawer({ freeTextMedicationName })
  }

  const onSelectMedication = (medication: MedicationDetail) => {
    handleOpenAddMedicalDrawer({ medication })
  }

  const onSelectExistingMedication = (editMedication: chMedicationFragmentInline$data) => {
    handleCreateRecommendedMedication(editMedication.id)
  }

  return (
    appointmentId && (
      <Suspense fallback={<LoadingSpinner size={24} />}>
        <ChMedicationSearchWrapper
          includeTemplates
          className="max-h-87.5 overflow-hidden p-2"
          patientLanguage={patientLanguage}
          existingMedications={existingMedications}
          useMedicationIcon
          handleSelectMedication={onSelectMedication}
          handleFreeTextMedicationSelection={onSelectFreeTextMedication}
          handleSelectExistingMedication={onSelectExistingMedication}
          patientId={patientId}
          appointmentId={appointmentId}
          diagnosisCode={diagnosisCode}
        />
      </Suspense>
    )
  )
}
